import React, { FC, useCallback, useState } from "react";
import { Popover } from "react-tiny-popover";
import { NavLink } from "react-router-dom";
import { useDispatch } from "react-redux";

import { StatusPill } from "../pills";
import {
  ListControl,
  Button,
  Icon,
  Avatar,
  Label,
} from "../../components/common";
import { InvoiceDropdown } from "../../components/menu";
import { DEFAULT_FIAT_CURRENCY, STATUS_CONFIG } from "../../constants";
import { ROUTES } from "../../routes";
import { resetStep } from "../../store/reducers/invoice";
import { StatusSet } from "../../types/GlobalTypes";
import { InvoiceShort, InvoiceUserType } from "../../types/InvoiceTypes";
import { formatAmount } from "../../utils/numbers/money";
import { formatDate, DateFormat } from "../../utils/date";
import { isMobileMedia } from "../../utils/media";

const STATUS_PROPS = STATUS_CONFIG[StatusSet.INVOICE];

type Props = {
  data: InvoiceShort[];
  userIs?: InvoiceUserType;
};

export const InvoicesTable: FC<Props> = ({
  data,
  userIs = InvoiceUserType.ISSUER,
}) => {
  const [dropdownVisible, setDropdownVisible] = useState("");
  const dispatch = useDispatch();

  const dropdownHandler = useCallback(
    (
      e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
      invoice: InvoiceShort
    ) => {
      e.preventDefault();
      setDropdownVisible(invoice.id);
    },
    []
  );

  const resetSteppedInvoice = useCallback(() => {
    if (isMobileMedia()) {
      dispatch(resetStep());
    }
  }, [dispatch]);

  return (
    <div
      className="w-full flex flex-col items-stretch"
      data-test="invoices-table"
    >
      {data.map((invoice) => (
        <ListControl
          key={invoice.id}
          className="flex flex-col min-w-full items-stretch justify-stretch -mx-4"
        >
          <NavLink
            to={ROUTES.INVOICE.replace(":id", invoice.id)}
            className="border-t border-gray-50 flex flex-row w-full items-center justify-between gap-x-8 hover:bg-slate-100 py-6 px-4"
            data-test="invoice-table-row"
            onClick={resetSteppedInvoice}
          >
            <Avatar
              type="business"
              title={
                userIs === InvoiceUserType.ISSUER
                  ? invoice.payer_name || "New invoice"
                  : invoice.issuer_name || "New invoice"
              }
              className="flex-none"
              icon={
                isMobileMedia() ? STATUS_PROPS[invoice.status].icon : undefined
              }
              iconColor={
                isMobileMedia() ? STATUS_PROPS[invoice.status].color : undefined
              }
            />

            <div className="mr-auto flex flex-col gap-y-2 flex-auto max-w-[380px]">
              <div
                className="theme-text-default w-full overflow-hidden text-ellipsis"
                data-test="bussiness-name"
              >
                {(userIs === InvoiceUserType.ISSUER
                  ? invoice.payer_name
                  : invoice.issuer_name) || "New invoice"}
              </div>

              <div
                className="text-xs md:text-base theme-text-secondary"
                data-test="invoice-number"
              >
                #{invoice.number}
                <span className="md:hidden text-xs md:text-base">
                  • Due on {formatDate(invoice?.due_at, DateFormat.d_MMM)}
                </span>
              </div>

              <div className="md:hidden text-xs">
                <span className={STATUS_PROPS[invoice.status].color}>
                  {STATUS_PROPS[invoice.status].text}
                </span>
              </div>
            </div>

            <div
              className="hidden md:block ml-auto flex-none"
              data-test="invoice-due-on"
            >
              <Label label="Due on" className="theme-text-default">
                {formatDate(invoice?.due_at)}
              </Label>
            </div>

            <div
              className="w-32 flex flex-col mr-4 text-right flex-none"
              data-test="invoice-amount"
            >
              <div className="theme-text-default">
                {formatAmount(Number(invoice.amount))}
                &nbsp;
                <span className="uppercase">
                  {invoice.currency || DEFAULT_FIAT_CURRENCY}
                </span>
              </div>

              <div className="theme-text-secondary text-xs md:text-base uppercase">
                <Icon
                  className="mb-2"
                  icon="Subdirectory"
                  size={isMobileMedia() ? 16 : 24}
                  disableFill
                />
                {invoice.outgoing_currency}
              </div>
            </div>

            <div
              className="hidden md:block w-36 flex-none"
              data-test="invoice-status"
            >
              <StatusPill set={StatusSet.INVOICE} status={invoice.status} />
            </div>

            <Popover
              isOpen={dropdownVisible === invoice.id}
              positions={["bottom", "left"]}
              content={
                <InvoiceDropdown
                  data={invoice}
                  closeDropdown={() => setDropdownVisible("")}
                />
              }
              containerClassName="z-50"
              padding={10}
              align="end"
              onClickOutside={() => setDropdownVisible("")}
            >
              <div className="hidden md:block relative flex-none">
                <Button
                  data-test="invoice-three-dots-options-btn"
                  variant={Button.variant.TERTIARY}
                  onClick={(
                    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
                  ) => dropdownHandler(e, invoice)}
                  icon
                >
                  <Icon icon="Dot-Menu" size={24} disableFill />
                </Button>
              </div>
            </Popover>
          </NavLink>
        </ListControl>
      ))}
    </div>
  );
};
